import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Shield, Send, Upload, CheckCircle, Camera, Loader, X, AlertTriangle, ChevronRight } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TYPE_LABELS = {
  commercial_property: "Commercial Property",
  workers_comp: "Workers' Compensation",
  habitational: "Habitational",
  general_liability: "General Liability",
  commercial_auto: "Commercial Auto",
};

function FindingBubble({ content }) {
  const findingRegex = /---FINDING---([\s\S]*?)---END FINDING---/g;
  const parts = [];
  let lastIdx = 0;
  let match;
  while ((match = findingRegex.exec(content)) !== null) {
    if (match.index > lastIdx) parts.push({ type: "text", content: content.slice(lastIdx, match.index) });
    parts.push({ type: "finding", content: match[1].trim() });
    lastIdx = match.index + match[0].length;
  }
  if (lastIdx < content.length) parts.push({ type: "text", content: content.slice(lastIdx) });

  return (
    <div className="space-y-2">
      {parts.map((p, i) => {
        if (p.type === "text") return <p key={i} className="whitespace-pre-line text-sm leading-relaxed">{p.content}</p>;
        const lines = p.content.split("\n");
        const parseFinding = (lines) => {
          const obj = {};
          lines.forEach(l => {
            const [k, ...rest] = l.split(":");
            if (k && rest.length) obj[k.trim().toLowerCase()] = rest.join(":").trim();
          });
          return obj;
        };
        const f = parseFinding(lines);
        const sevColors = { critical: "border-red-400 bg-red-50", high: "border-orange-400 bg-orange-50", medium: "border-amber-400 bg-amber-50", low: "border-green-400 bg-green-50" };
        const sev = (f.severity || "medium").toLowerCase();
        return (
          <div key={i} className={`rounded-lg border-l-4 p-3 ${sevColors[sev] || sevColors.medium}`}>
            <div className="flex items-center gap-2 mb-1">
              <AlertTriangle className="w-3.5 h-3.5" />
              <span className="text-xs font-bold uppercase">{f.severity || "Finding"}</span>
              {f.confidence && <span className="text-xs bg-white/70 px-1.5 rounded">Confidence: {f.confidence}</span>}
            </div>
            {f.category && <p className="text-xs font-medium mb-1">{f.category}</p>}
            {f.issue && <p className="text-xs">{f.issue}</p>}
            {f.recommendation && <p className="text-xs mt-1 opacity-80"><span className="font-medium">Fix:</span> {f.recommendation}</p>}
            {f.guideline && <p className="text-xs mt-1 opacity-60 italic">{f.guideline}</p>}
          </div>
        );
      })}
    </div>
  );
}

export default function InspectionPortal() {
  const { token } = useParams();
  const [inspection, setInspection] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  const messagesEndRef = useRef(null);
  const fileRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/insured/${token}`)
      .then(res => {
        setInspection(res.data);
        setMessages(res.data.messages || []);
        if (res.data.status === "completed" || res.data.status === "finalized") setSubmitted(true);
      })
      .catch(err => setError(err.response?.data?.detail || "Inspection not found or link expired"))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, sending]);

  const sendMessage = async (e) => {
    e?.preventDefault();
    if (!input.trim() || sending) return;
    const text = input.trim();
    setInput("");

    const tempMsg = { id: `tmp-${Date.now()}`, role: "user", content: text, timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, tempMsg]);
    setSending(true);

    try {
      const res = await axios.post(`${API}/insured/${token}/chat`, { message: text });
      setMessages(prev => [...prev, res.data]);
    } catch (err) {
      setError("Failed to send message. Please try again.");
      setMessages(prev => prev.filter(m => m.id !== tempMsg.id));
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("photo", file);
    fd.append("message", `I've uploaded a photo of: ${file.name}`);
    try {
      const res = await axios.post(`${API}/insured/${token}/upload-photo`, fd, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setMessages(prev => [...prev, res.data.user_message, res.data.ai_response]);
    } catch (err) {
      setError("Photo upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/insured/${token}/submit`, {});
      setSubmitted(true);
      setShowSubmitConfirm(false);
    } catch (err) {
      setError("Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center">
        <div className="w-12 h-12 bg-navy rounded-xl flex items-center justify-center mx-auto mb-3">
          <Shield className="w-6 h-6 text-white animate-pulse" />
        </div>
        <p className="text-slate-500 text-sm">Loading your inspection...</p>
      </div>
    </div>
  );

  if (error && !inspection) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center max-w-md shadow-card">
        <AlertTriangle className="w-10 h-10 text-red-400 mx-auto mb-3" />
        <h2 className="font-heading font-bold text-slate-800 text-xl mb-2">Inspection Not Found</h2>
        <p className="text-slate-500 text-sm">{error}</p>
            <p className="text-xs text-slate-400">This link may have expired or is invalid. Please contact your insurance company for a new survey link.</p>
      </div>
    </div>
  );

  if (submitted) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center max-w-md shadow-card animate-bounce-in">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-8 h-8 text-green-600" />
        </div>
        <h2 className="font-heading font-bold text-slate-800 text-2xl mb-2">Inspection Complete!</h2>
        <p className="text-slate-600 text-sm leading-relaxed mb-4">
          Thank you for completing the loss control survey for <strong>{inspection?.insured_name}</strong>.
          Your responses have been submitted to {inspection?.carrier_name}.
        </p>
        <p className="text-slate-400 text-xs">A loss control professional will review your responses and contact you with any follow-up items.</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" data-testid="inspection-portal">
      {/* Header */}
      <header className="bg-navy px-4 py-4 flex-shrink-0">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-white/15 rounded-lg flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="font-heading font-bold text-white text-sm">{inspection?.carrier_name || "RiskSurvey.ai"}</div>
              <div className="text-white/50 text-xs">{TYPE_LABELS[inspection?.inspection_type] || "Loss Control Survey"}</div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-white text-xs font-medium">{inspection?.insured_name}</div>
            <div className="text-white/50 text-xs">{inspection?.property_address}</div>
          </div>
        </div>
      </header>

      {/* Progress hint */}
      <div className="bg-white border-b border-slate-200 px-4 py-2 flex-shrink-0">
        <div className="max-w-2xl mx-auto flex items-center justify-between text-xs text-slate-500">
          <span>{messages.length} messages exchanged</span>
          <button
            data-testid="submit-inspection-btn"
            onClick={() => setShowSubmitConfirm(true)}
            className="flex items-center gap-1 text-navy font-medium hover:text-navy-dark transition-colors"
          >
            Complete Inspection <ChevronRight className="w-3 h-3" />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto chat-scroll px-4 py-6">
        <div className="max-w-2xl mx-auto space-y-4">
          {messages.map((msg) => (
            <div key={msg.id} className={`flex ${msg.role === "assistant" ? "justify-start" : "justify-end"} animate-slide-up`} data-testid={`msg-${msg.role}`}>
              {msg.role === "assistant" && (
                <div className="w-7 h-7 bg-navy rounded-full flex items-center justify-center mr-2 mt-1 flex-shrink-0">
                  <Shield className="w-3.5 h-3.5 text-white" />
                </div>
              )}
              <div className={`max-w-lg px-4 py-3 shadow-sm ${msg.role === "assistant" ? "chat-bubble-ai" : "chat-bubble-user"}`}>
                {msg.role === "assistant" ? (
                  <FindingBubble content={msg.content} />
                ) : (
                  <div>
                    <p className="text-sm whitespace-pre-line">{msg.content}</p>
                    {msg.photo_urls?.length > 0 && (
                      <div className="flex gap-2 mt-2 flex-wrap">
                        {msg.photo_urls.map((url, i) => (
                          <img key={i} src={`${BACKEND_URL}${url}`} alt="uploaded" className="h-20 w-20 object-cover rounded-lg border border-slate-200" />
                        ))}
                      </div>
                    )}
                  </div>
                )}
                <div className="text-right mt-1">
                  <span className={`text-xs ${msg.role === "assistant" ? "text-white/40" : "text-slate-400"}`}>
                    {new Date(msg.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                  </span>
                </div>
              </div>
            </div>
          ))}

          {(sending || uploading) && (
            <div className="flex justify-start animate-slide-up">
              <div className="w-7 h-7 bg-navy rounded-full flex items-center justify-center mr-2 mt-1 flex-shrink-0">
                <Shield className="w-3.5 h-3.5 text-white" />
              </div>
              <div className="chat-bubble-ai px-4 py-3">
                <div className="flex items-center gap-2">
                  <Loader className="w-4 h-4 animate-spin" />
                  <span className="text-sm text-white/80">{uploading ? "Analyzing photo..." : "Analyzing response..."}</span>
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="flex justify-center">
              <div className="flex items-center gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-red-600 text-xs">
                <AlertTriangle className="w-3.5 h-3.5" />
                {error}
                <button onClick={() => setError("")}><X className="w-3 h-3" /></button>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input area */}
      <div className="bg-white border-t border-slate-200 px-4 py-3 flex-shrink-0">
        <div className="max-w-2xl mx-auto">
          <form onSubmit={sendMessage} className="flex items-end gap-2">
            <div className="flex-1 border border-slate-200 rounded-xl overflow-hidden focus-within:ring-2 focus-within:ring-navy/30 focus-within:border-navy transition-all">
              <textarea
                data-testid="chat-input"
                ref={inputRef}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                placeholder="Type your response... (Enter to send)"
                rows={2}
                disabled={sending || uploading}
                className="w-full px-4 py-3 text-sm resize-none focus:outline-none"
              />
            </div>
            <div className="flex flex-col gap-2">
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={e => { if (e.target.files[0]) uploadPhoto(e.target.files[0]); }} data-testid="photo-file-input" />
              <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
                className="w-10 h-10 flex items-center justify-center bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-xl transition-colors disabled:opacity-50" data-testid="upload-photo-btn" title="Upload photo">
                <Camera className="w-4 h-4" />
              </button>
              <button type="submit" disabled={!input.trim() || sending}
                className="w-10 h-10 flex items-center justify-center bg-navy hover:bg-navy-dark text-white rounded-xl transition-colors disabled:opacity-50" data-testid="send-message-btn">
                <Send className="w-4 h-4" />
              </button>
            </div>
          </form>
          <p className="text-xs text-slate-400 mt-2 text-center">
            This survey is confidential. Your responses are being reviewed by {inspection?.carrier_name}.
          </p>
        </div>
      </div>

      {/* Submit Confirmation Modal */}
      {showSubmitConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 animate-fade-in">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl animate-bounce-in" data-testid="submit-confirm-modal">
            <CheckCircle className="w-10 h-10 text-navy mx-auto mb-3" />
            <h3 className="font-heading font-bold text-slate-800 text-lg text-center mb-2">Submit Survey?</h3>
            <p className="text-slate-500 text-sm text-center mb-6">
              Once submitted, you won't be able to add more responses. Make sure you've answered all questions thoroughly.
            </p>
            <div className="flex gap-3">
              <button onClick={() => setShowSubmitConfirm(false)}
                className="flex-1 h-10 border border-slate-200 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors">
                Go Back
              </button>
              <button onClick={handleSubmit} disabled={submitting}
                data-testid="confirm-submit-btn"
                className="flex-1 h-10 bg-navy text-white rounded-lg text-sm font-medium hover:bg-navy-dark transition-colors disabled:opacity-60">
                {submitting ? "Submitting..." : "Submit Inspection"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
